import { ChevronDown, Target } from "lucide-react";
import type { GoalSnapshot } from "@lumen/shared";
import { t } from "../../i18n";

export function GoalPanel({
  goal,
  running,
  waiting,
  failed,
  onStop,
  onContinue,
}: {
  goal: GoalSnapshot;
  running: boolean;
  waiting?: boolean;
  failed?: boolean;
  onStop: () => void;
  onContinue?: () => void;
}) {
  const label = waiting
    ? t("Waiting for you")
    : failed
      ? t("Goal interrupted")
      : goal.status === "completed"
        ? t("Goal achieved")
        : goal.status === "blocked"
          ? t("Goal blocked")
          : goal.status === "limit-reached"
            ? t("Goal turn limit reached")
            : goal.status === "paused" || !running
              ? t("Goal paused")
              : t("Goal active");
  const canContinue =
    !running &&
    !waiting &&
    goal.status !== "completed" &&
    goal.status !== "blocked";
  return (
    <div
      className="mx-auto w-full max-w-3xl px-4 pb-2"
      data-testid="goal-panel"
    >
      <details className="group rounded-xl border border-lumen-200 bg-lumen-50/60 text-stone-700 dark:border-lumen-800 dark:bg-lumen-950/30 dark:text-stone-200">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-xs focus-visible:outline-2 focus-visible:outline-lumen-600 [&::-webkit-details-marker]:hidden">
          <Target size={15} aria-hidden="true" className="shrink-0" />
          <span className="min-w-0 flex-1">
            <strong role="status" className="block">
              {label}
            </strong>
            <span className="block truncate">{goal.objective}</span>
          </span>
          <span className="shrink-0 text-stone-500 dark:text-stone-400">
            {t("Goal checks: {{count}}", { count: goal.turns })}
          </span>
          <ChevronDown
            size={14}
            aria-hidden="true"
            className="shrink-0 group-open:rotate-180"
          />
        </summary>
        <div className="space-y-2 border-t border-lumen-200 px-3 py-2 text-xs dark:border-lumen-800">
          <p className="max-h-28 overflow-y-auto break-words whitespace-pre-wrap">
            {goal.objective}
          </p>
          <p className="text-stone-600 dark:text-stone-300">
            {goal.reason ||
              t(
                "The outcome is checked after each turn. You can stop at any time.",
              )}
          </p>
          {goal.maxTurns && (
            <p className="text-stone-600 dark:text-stone-300">
              {t("Run limit: {{count}} turns", { count: goal.maxTurns })}
            </p>
          )}
        </div>
      </details>
      {((running && !waiting) || (canContinue && onContinue)) && (
        <div className="mt-1 flex justify-end">
          <button
            type="button"
            className="button min-h-9 px-3 text-xs"
            onClick={running ? onStop : onContinue}
          >
            {running ? t("Stop goal") : t("Continue goal")}
          </button>
        </div>
      )}
    </div>
  );
}
